import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Tooltip, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import type { GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { ProtoSelectStandalone } from 'src/components/flow-editor/ProtoSelectStandalone'
import { EmptyTableMessage } from 'src/components/payments-ops/EmptyTableMessage'
import { JsonPreviewModal } from 'src/components/payments-ops/JsonPreviewModal'
import { Button } from 'src/components/shared/Button'
import { ConfirmActionModal } from 'src/components/shared/ConfirmActionModal'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { SectionCard } from 'src/components/shared/SectionCard'
import {
  AdminBulkReplayCapturedEvents,
  AdminFetchCapturedEvents,
  AdminReplayAllCapturedEvents,
  AdminReplayCapturedEvent,
} from 'src/constants/actionTypes'
import { buildCapturedEventColumns } from 'src/constants/payments-ops/capturedEventsGrid'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { CAPTURED_EVENTS_PAGE_CAP } from 'src/constants/payments-ops/constants'
import {
  CapturedEventSource,
  CapturedEventStatus,
  KillswitchFeature,
} from 'src/constants/payments-ops/enums'
import {
  capturedEventSourceLabels,
  capturedEventStatusLabels,
} from 'src/constants/payments-ops/labels'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { MenuActionItem } from 'src/interfaces/ui'
import type { CapturedEvent, FetchCapturedEventsPayload } from 'src/interfaces/usBankKillswitch'
import {
  getCapturedEventReplaying,
  getCapturedEvents,
  getCapturedEventsError,
  getCapturedEventsLoading,
} from 'src/selectors/usBankKillswitch'
import { addS } from 'src/utils/baseUtils'
import { useKillswitchStates } from 'src/utils/hooks/paymentsOps/useKillswitchStates'
import { useKillswitchStatusKnown } from 'src/utils/hooks/paymentsOps/useKillswitchStatusKnown'
import { usePartnerNameMap } from 'src/utils/hooks/paymentsOps/usePartnerNameMap'
import {
  emptySelection,
  isDiscardableStatus,
  isReplayableStatus,
  killswitchFeatureForSource,
  resolveReplayMessage,
  resolveSelectedIds,
} from 'src/utils/paymentsOps/capturedEvents'
import { DiscardEventModal } from './DiscardEventModal'

type PendingConfirmation =
  | { kind: 'single'; event: CapturedEvent }
  | { kind: 'selected'; eventIds: string[] }
  | { kind: 'all' }

interface ConfirmationCopy {
  title: string
  description: string
  confirmLabel: string
}

const ANY_VALUE = ''
const REPLAY_BLOCKED_REASON =
  'Turn off the killswitch first — a replay now would be captured again.'
const PARTNER_REPLAY_BLOCKED_REASON =
  "This partner's killswitch is on — a replay now would be captured again."
const PARTNER_REPLAY_ALL_BLOCKED_REASON =
  'A partner killswitch is on — its events would be captured again. Replay selected events, or lift it first.'

const STATUS_UNAVAILABLE_REASON = 'Killswitch status unavailable — reload the page'
const STATUS_NO_ACCESS_REASON = 'Killswitch status unavailable — requires feature flags access.'
const NOT_REPLAYABLE_REASON = 'Only pending or failed events can be replayed.'

const getRowId = (row: CapturedEvent): string => {
  return row.id
}

const renderEmpty = () => {
  return <EmptyTableMessage message="No captured events for these filters." />
}

const describeConfirmation = (pending: PendingConfirmation | null): ConfirmationCopy => {
  if (pending?.kind === 'single') {
    const sourceLabel = capturedEventSourceLabels[pending.event.source] ?? pending.event.source

    return {
      title: 'Replay event?',
      description: `Replay ${sourceLabel} event ${pending.event.id} against the live payments backend?`,
      confirmLabel: 'Replay event',
    }
  }

  if (pending?.kind === 'selected') {
    const count = pending.eventIds.length

    return {
      title: 'Replay selected events?',
      description: `Replay ${count} selected ${addS(
        count,
        'event',
      )} against the live payments backend? They are processed asynchronously.`,
      confirmLabel: `Replay ${count}`,
    }
  }

  if (pending?.kind === 'all') {
    return {
      title: 'Replay all pending events?',
      description: `Replay EVERY pending captured event — including any beyond the newest ${CAPTURED_EVENTS_PAGE_CAP} shown — against the live payments backend? They are processed asynchronously, oldest first.`,
      confirmLabel: 'Replay all pending',
    }
  }

  return { title: '', description: '', confirmLabel: '' }
}

const CapturedEventsSectionComponent = () => {
  const snackbar = useSnackbar()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const nameMap = usePartnerNameMap()
  const { payments, webhooks } = useKillswitchStates()
  // The replay endpoint does not enforce the killswitch (only refund execution does), so replay
  // is allowed only when the status is actually known: without `feature_flags_view` the flags
  // are never read, and the empty rows must not read as "no switch on".
  const canViewFlags = useHasPermission(InternalPermission.FeatureFlagsView)
  const areFlagsLoaded = useKillswitchStatusKnown()
  const isStatusKnown = canViewFlags && areFlagsLoaded
  const statusUnavailableReason = canViewFlags ? STATUS_UNAVAILABLE_REASON : STATUS_NO_ACCESS_REASON
  // Replay, Discard, Replay selected and Replay all pending all POST under
  // `us_bank_captured_events_replay`; without it the section is a read-only list (spec §7.5).
  const canReplay = useHasPermission(InternalPermission.UsBankCapturedEventsReplay)

  const events = useSelector(getCapturedEvents)
  const isLoading = useSelector(getCapturedEventsLoading)
  const listError = useSelector(getCapturedEventsError)
  const isBusy = useSelector(getCapturedEventReplaying)

  const [status, setStatus] = useState<CapturedEventStatus | null>(CapturedEventStatus.Pending)
  const [source, setSource] = useState<CapturedEventSource | null>(null)
  const [selection, setSelection] = useState<GridRowSelectionModel>(emptySelection)
  const [pending, setPending] = useState<PendingConfirmation | null>(null)
  const [discarding, setDiscarding] = useState<CapturedEvent | null>(null)
  const [payloadEvent, setPayloadEvent] = useState<CapturedEvent | null>(null)
  const [refreshToken, setRefreshToken] = useState<number>(0)

  const request = useMemo<FetchCapturedEventsPayload>(() => {
    return { status: status ?? undefined, source: source ?? undefined }
  }, [source, status])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchCapturedEvents.request(request))
    setSelection(emptySelection())
  }, [asyncDispatchWithErrorToast, isAuthenticated, refreshToken, request])

  // The backend checks `partnerFlag?.enabled || globalFlag?.enabled`, so a per-partner row stops
  // that partner's events exactly like the global switch does.
  const getEventBlockedReason = useCallback(
    (event: CapturedEvent): string | null => {
      const featureState =
        killswitchFeatureForSource(event.source) === KillswitchFeature.Webhooks
          ? webhooks
          : payments

      if (featureState.isGloballyActive) {
        return REPLAY_BLOCKED_REASON
      }

      if (event.partnerId !== null && featureState.activePartnerIds.includes(event.partnerId)) {
        return PARTNER_REPLAY_BLOCKED_REASON
      }

      return null
    },
    [payments, webhooks],
  )

  const isAnySwitchActive = payments.isGloballyActive || webhooks.isGloballyActive
  // Replay-all replays EVERY pending event server-side, including those of a stopped partner.
  const isAnyPartnerSwitchActive =
    payments.activePartnerIds.length > 0 || webhooks.activePartnerIds.length > 0
  // Replay-all is blocked by an active switch, and by not knowing whether one is active.
  const isReplayAllBlocked = isAnySwitchActive || isAnyPartnerSwitchActive || !isStatusKnown

  const isEventReplayable = useCallback(
    (event: CapturedEvent): boolean => {
      return (
        isStatusKnown && isReplayableStatus(event.status) && getEventBlockedReason(event) === null
      )
    },
    [getEventBlockedReason, isStatusKnown],
  )

  const selectableIds = useMemo(() => {
    return events
      .filter((event) => {
        return isEventReplayable(event)
      })
      .map((event) => {
        return event.id
      })
  }, [events, isEventReplayable])

  const selectedIds = useMemo(() => {
    return resolveSelectedIds(selection, selectableIds)
  }, [selectableIds, selection])

  const columns = useMemo(() => {
    const buildActions = (event: CapturedEvent): MenuActionItem[] => {
      const viewPayloadAction: MenuActionItem = {
        label: 'View payload',
        onClick: () => {
          setPayloadEvent(event)
        },
      }

      if (!canReplay) {
        return [viewPayloadAction]
      }

      const blockedReason = getEventBlockedReason(event)
      const isBlocked = blockedReason !== null
      const isReplayable = isReplayableStatus(event.status)
      let replayDisabledReason: string | undefined

      // Most specific first: a replayed event is not replayable whatever the switches say.
      if (!isReplayable) {
        replayDisabledReason = NOT_REPLAYABLE_REASON
      } else if (blockedReason !== null) {
        replayDisabledReason = blockedReason
      } else if (!isStatusKnown) {
        replayDisabledReason = statusUnavailableReason
      }

      return [
        {
          label: 'Replay',
          disabled: isBusy || !isReplayable || isBlocked || !isStatusKnown,
          disabledReason: replayDisabledReason,
          onClick: () => {
            setPending({ kind: 'single', event })
          },
        },
        {
          label: 'Discard…',
          disabled: isBusy || !isDiscardableStatus(event.status),
          disabledReason: 'Only pending events can be discarded.',
          onClick: () => {
            setDiscarding(event)
          },
        },
        viewPayloadAction,
      ]
    }

    return buildCapturedEventColumns({ nameMap, buildActions })
  }, [canReplay, getEventBlockedReason, isBusy, isStatusKnown, nameMap, statusUnavailableReason])

  const isRowSelectable = useCallback(
    (params: GridRowParams<CapturedEvent>) => {
      return isEventReplayable(params.row)
    },
    [isEventReplayable],
  )

  const statusOptions = useMemo(() => {
    const options = Object.values(CapturedEventStatus).map((value) => {
      return { value, label: capturedEventStatusLabels[value].label }
    })

    return [{ value: ANY_VALUE, label: 'Any status' }, ...options]
  }, [])

  const sourceOptions = useMemo(() => {
    const options = Object.values(CapturedEventSource).map((value) => {
      return { value, label: capturedEventSourceLabels[value] }
    })

    return [{ value: ANY_VALUE, label: 'Any source' }, ...options]
  }, [])

  const handleStatusChange = useCallback((value: string | number) => {
    setStatus(value === ANY_VALUE ? null : (value as CapturedEventStatus))
  }, [])

  const handleSourceChange = useCallback((value: string | number) => {
    setSource(value === ANY_VALUE ? null : (value as CapturedEventSource))
  }, [])

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  const handleReplaySelected = useCallback(() => {
    setPending({ kind: 'selected', eventIds: selectedIds })
  }, [selectedIds])

  const handleReplayAll = useCallback(() => {
    setPending({ kind: 'all' })
  }, [])

  const handleCloseConfirm = useCallback(() => {
    // The replay is in flight: closing now would hide its outcome.
    if (isBusy) {
      return
    }

    setPending(null)
  }, [isBusy])

  const handleCloseDiscard = useCallback(() => {
    setDiscarding(null)
  }, [])

  const handleClosePayload = useCallback(() => {
    setPayloadEvent(null)
  }, [])

  const handleConfirm = useCallback(async () => {
    if (!pending) {
      return
    }

    let result: unknown = null

    if (pending.kind === 'single') {
      result = await asyncDispatchWithErrorToast(
        AdminReplayCapturedEvent.request({ eventId: pending.event.id, refetch: request }),
      )
    }

    if (pending.kind === 'selected') {
      result = await asyncDispatchWithErrorToast(
        AdminBulkReplayCapturedEvents.request({ eventIds: pending.eventIds, refetch: request }),
      )
    }

    if (pending.kind === 'all') {
      result = await asyncDispatchWithErrorToast(
        AdminReplayAllCapturedEvents.request({ refetch: request }),
      )
    }

    setPending(null)

    if (result) {
      setSelection(emptySelection())
      // "Replayed" means re-published for processing, not processed — say "queued".
      snackbar?.enqueueSnackbar(`Queued for replay. ${resolveReplayMessage(result)}`.trim(), {
        variant: 'success',
      })
    }
  }, [asyncDispatchWithErrorToast, pending, request, snackbar])

  const confirmation = describeConfirmation(pending)
  let replayAllReason = ''

  if (isAnySwitchActive) {
    replayAllReason = REPLAY_BLOCKED_REASON
  } else if (isAnyPartnerSwitchActive) {
    replayAllReason = PARTNER_REPLAY_ALL_BLOCKED_REASON
  } else if (!isStatusKnown) {
    replayAllReason = statusUnavailableReason
  }

  return (
    <Fragment>
      <SectionCard
        title="Captured events"
        subtitle={
          events.length >= CAPTURED_EVENTS_PAGE_CAP
            ? `Newest ${CAPTURED_EVENTS_PAGE_CAP} shown — the backend returns no more`
            : `${events.length} ${addS(events.length, 'event')}`
        }
        dataTestId="captured-events-section"
      >
        <div css={tw`flex flex-col gap-3`}>
          <div css={tw`flex flex-row flex-wrap items-end gap-4`}>
            <div css={tw`flex w-[180px] flex-col`}>
              <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
                Status
              </Typography>
              <ProtoSelectStandalone
                value={status ?? ANY_VALUE}
                options={statusOptions}
                onValueChange={handleStatusChange}
              />
            </div>
            <div css={tw`flex w-[220px] flex-col`}>
              <Typography variant="h6" css={tw`mb-1 text-shades-400`}>
                Source
              </Typography>
              <ProtoSelectStandalone
                value={source ?? ANY_VALUE}
                options={sourceOptions}
                onValueChange={handleSourceChange}
              />
            </div>
            <div css={tw`ml-auto flex flex-row gap-2 pb-1`}>
              <Button size="small" color="white" onClick={handleRefresh}>
                Refresh
              </Button>
              {canReplay ? (
                <Fragment>
                  <Button
                    size="small"
                    color="white"
                    disabled={selectedIds.length === 0 || isBusy}
                    onClick={handleReplaySelected}
                    data-testid="captured-events-replay-selected"
                  >
                    {`Replay selected (${selectedIds.length})`}
                  </Button>
                  <Tooltip title={replayAllReason}>
                    <span>
                      <Button
                        size="small"
                        color="dark"
                        disabled={isReplayAllBlocked || isBusy}
                        onClick={handleReplayAll}
                        data-testid="captured-events-replay-all"
                      >
                        Replay all pending…
                      </Button>
                    </span>
                  </Tooltip>
                </Fragment>
              ) : null}
            </div>
          </div>
          {listError ? (
            <Typography variant="body2" css={tw`text-critical-500`}>
              {listError}
            </Typography>
          ) : null}
          <GridTable<CapturedEvent>
            rows={events}
            columns={columns}
            getRowId={getRowId}
            isLoading={isLoading}
            autoHeight
            isStandalone
            // The checkboxes only feed "Replay selected" — hidden with it.
            checkboxSelection={canReplay}
            isRowSelectable={isRowSelectable}
            rowSelectionModel={selection}
            onRowSelectionModelChange={setSelection}
            sortingMode="client"
            hideFooter
            EmptyElement={renderEmpty}
            tableDataTestId="captured-events-table"
            rowDataTestIdPrefix="captured-events"
          />
        </div>
      </SectionCard>
      <ConfirmActionModal
        isOpen={pending !== null}
        title={confirmation.title}
        description={confirmation.description}
        acknowledgement={
          pending?.kind === 'all' ? 'I understand these hit the live payments backend.' : undefined
        }
        confirmLabel={confirmation.confirmLabel}
        isSubmitting={isBusy}
        onConfirm={handleConfirm}
        onClose={handleCloseConfirm}
        confirmDataTestId="captured-events-confirm"
      />
      <DiscardEventModal event={discarding} refetch={request} onClose={handleCloseDiscard} />
      <JsonPreviewModal
        title="Captured event payload"
        value={payloadEvent ? payloadEvent.payload : null}
        onClose={handleClosePayload}
      />
    </Fragment>
  )
}

export const CapturedEventsSection = memo(CapturedEventsSectionComponent)
